"use client";
import { useMemo } from "react";
import {
  MotionImage,
  MotionLink,
  MotionLinkProps,
  MotionStack,
  MotionTypographyProps,
} from "@/commonComponents/motion-components";
import SectionWrapper, { basePx } from "@/commonComponents/section-wrapper";
import { EnhancedSwiperProps } from "@/commonComponents/enhanced-swiper";
import { Autoplay } from "swiper/modules";
import { MaterialImage } from "@/commonComponents/material-components";
import { StaticImageData } from "next/image";
import { ArrowForwardIos } from "@mui/icons-material";
import { H6_2 } from "@/styles/theme/components/typography.fontvariant";
import {
  bottomTextStagger,
  iconStagger,
  iconStaggerChildren,
  textStaggerChildren,
} from "@/commonComponents/animations";
import "../../node_modules/swiper/modules/autoplay.min.css";
import Link from "@cc/link";
import { Theme, useMediaQuery } from "@mui/material";
import { filter } from "lodash";
import EnhancedSwiper from "./enhanced-swiper";

export interface DiscoverSectionProps {
  title?: string;
  TitleTypographyProps?: MotionTypographyProps;
  linkProps?: MotionLinkProps; // Todo update in other components
  ImageData: Array<ImageSliceProps>;
  hasSocials?: boolean;
  bgcolor?: string;
  color?: string;
  clickableSlides?: boolean;
  onImageClick?: (index: number) => void;
  links?: {
    icon: string;
    url: string;
    name: string;
    type?: string;
  }[];
  navId?: string;
}

export interface ImageSliceProps {
  image: StaticImageData | string;
  clickable?: boolean;
}

const DiscoverSection = (props: DiscoverSectionProps) => {
  const {
    title,
    TitleTypographyProps,
    linkProps,
    ImageData,
    hasSocials,
    bgcolor,
    color,
    clickableSlides,
    onImageClick,
    links = [],
    navId,
  } = props;

  const matches = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm_md"),
  );

  const socialLinks = useMemo(() => {
    const size = matches ? 32 : 48;
    const tempLinks = links.map((item, idx) => {
      if (!item.url || item.url === "#") return null;
      return (
        <Link
          key={idx}
          href={item.url}
          title={item.name}
          target={`${item.type}` || "_blank"}
        >
          <MotionImage
            src={item.icon}
            alt={item.name}
            width={size}
            height={size}
            sx={{ width: size, height: size }}
            variants={iconStaggerChildren}
          />
        </Link>
      );
    });
    return filter(tempLinks, (item) => !!item);
  }, [links, matches]);

  return (
    <SectionWrapper
      navId={navId || "gallery"}
      SectionProps={{
        bgcolor: bgcolor || "primary.500",
        id: "gallery",
      }}
      color={color || "secondary.main"}
      SectionHeaderProps={{
        title: title,
        TitleTypographyProps: {
          variant: "h3",
          ...TitleTypographyProps,
        },
      }}
    >
      <EnhancedSwiper
        data={ImageData.concat(ImageData)}
        SlideComponent={ImageSlice}
        SlideComponentProps={{
          clickable: clickableSlides,
        }}
        SlideWrapperProps={(index) => ({
          sx: {
            width: "auto",
            maxHeight: 484,
            mr: { xs: 2, md: 3 },
            overflow: "hidden",
          },
          onClick: () => onImageClick && onImageClick(index),
        })}
        Slots={{
          ContainerEndChildren: hasSocials && (
            <>
              {linkProps && linkProps.href && linkProps.children && (
                <MotionLink
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: H6_2,
                    textDecoration: "none",
                  }}
                  disableRipple
                  variant={"text"}
                  color={"inherit"}
                  variants={textStaggerChildren}
                  endIcon={<ArrowForwardIos fontSize="inherit" />}
                  {...linkProps}
                />
              )}
              {links && (
                <MotionStack
                  direction={"row"}
                  gap={4}
                  mt={2}
                  variants={iconStagger}
                >
                  {socialLinks}
                </MotionStack>
              )}
            </>
          ),
          ContainerEndProps: {
            direction: { xs: "column-reverse", md: "row" },
            rowGap: 3,
            mt: { xs: 5, md: 7 },
            px: basePx,
            width: 1,
            alignItems: "center",
            justifyContent: "space-between",
            variants: bottomTextStagger(0),
            initial: "initial",
            whileInView: "animate",
            viewport: { once: true },
          },
        }}
        {...swiperProps}
      />
    </SectionWrapper>
  );
};

export default DiscoverSection;

const swiperProps: Omit<EnhancedSwiperProps, "data" | "SlideComponent"> = {
  slidesPerView: "auto",
  modules: [Autoplay],
  speed: 4500,
  loop: true,
  autoplay: {
    delay: 0,
    disableOnInteraction: false,
    reverseDirection: true,
  },
  sx: {
    overflow: {
      xs: "visible",
      wide: "hidden",
    },
    width: "100%",
    "& .swiper-wrapper": {
      transitionTimingFunction: "linear !important",
    },
  },
};

const ImageSlice = (props: ImageSliceProps) => {
  const { image, clickable } = props;
  return (
    <MaterialImage
      src={image}
      alt=""
      width={421}
      height={484}
      sx={{
        width: { xs: 250, md: 421 },
        height: { xs: 290, md: 484 },
        objectFit: "cover",
        "&:hover": clickable
          ? {
              transform: "scale(1.1)",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }
          : {},
      }}
    />
  );
};